/**
 * Simple US phone number validation and formatting utilities
 */

/**
 * Clean phone number by removing all non-digit characters
 */
export function cleanPhoneNumber(phone: string): string {
  if (!phone) return '';
  return phone.replace(/\D/g, '');
}

/**
 * Format US phone number to (123) 456-7890 format
 */
export function formatUSPhoneNumber(phone: string): string {
  if (!phone) return '';
  
  const cleaned = cleanPhoneNumber(phone);
  
  if (cleaned.length === 0) return '';
  if (cleaned.length <= 3) return cleaned;
  if (cleaned.length <= 6) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
  return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
}

/**
 * Validate US phone number (must be exactly 10 digits)
 */
export function isValidUSPhoneNumber(phone: string): boolean {
  if (!phone) return false;
  
  const cleaned = cleanPhoneNumber(phone);
  return cleaned.length === 10;
}

/**
 * Simple phone number validation
 */
export function validatePhoneNumberBasic(phone: string): { valid: boolean; message?: string } {
  if (!phone || phone.trim() === '') {
    return { valid: true }; // Allow empty for optional fields
  }
  
  const cleaned = cleanPhoneNumber(phone);
  
  if (cleaned.length > 10) {
    return { valid: false, message: 'Phone number cannot exceed 10 digits' };
  }
  
  if (cleaned.length > 0 && cleaned.length < 10) {
    return { valid: false, message: 'Please enter a valid 10-digit phone number' };
  }
  
  if (cleaned.length === 10) {
    return { valid: true };
  }
  
  return { valid: true };
}

/**
 * Validate and format phone number for US format
 */
export function validateAndFormatPhoneNumber(phone: string, format: 'us' = 'us') {
  const cleaned = cleanPhoneNumber(phone);
  const isValid = cleaned.length === 0 || cleaned.length === 10;
  
  return {
    isValid,
    formatted: formatUSPhoneNumber(phone),
    raw: cleaned
  };
}

/**
 * Phone number validation rule for vee-validate
 */
export function phoneValidationRule(value: string): boolean | string {
  const result = validatePhoneNumberBasic(value);
  return result.valid || result.message || 'Invalid phone number';
}

// Export commonly used patterns for direct use
export const PHONE_PATTERNS = {
  US: {
    MAX_LENGTH: 14, // (123) 456-7890 = 14 characters with formatting
    DIGITS_MAX: 10  // Maximum digits user can type
  }
};